import { createUuid } from '@notifi/uuid/browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { normalizeError } from '@/lib/api/errors';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';

interface NotificationOptions {
  ask: boolean;
  phone: string | null;
  email: string | null;
}
interface Prompt extends NotificationOptions {
  endpoint: string;
  requestId?: string;
  resend?: boolean;
  visit?: boolean;
}

export function useSchedulingNotificationPrompt() {
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [sms, setSms] = useState(false);
  const [email, setEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolveRef = useRef<((queued: boolean) => void) | null>(null);
  const sendingRef = useRef(false);
  useEffect(
    () => () => {
      resolveRef.current?.(false);
    },
    [],
  );

  const close = useCallback((queued = false) => {
    if (sendingRef.current) return;
    setPrompt(null);
    resolveRef.current?.(queued);
    resolveRef.current = null;
  }, []);

  const run = useCallback(async (id: string, config?: { visit?: boolean; resend?: boolean }) => {
    const endpoint = config?.visit
      ? `/appointments/${id}/confirmation-notification`
      : `/jobs/${id}/scheduling-notifications`;
    try {
      const options = await apiGet<NotificationOptions>(operationsClient, endpoint);
      if (!options.ask) return false;
      setSms(Boolean(options.phone));
      setEmail(Boolean(options.email));
      setError(null);
      return await new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setPrompt({
          ...options,
          ...config,
          endpoint,
          requestId: config?.visit ? createUuid() : undefined,
        });
      });
    } catch (error) {
      if (normalizeError(error).status === 403) return false;
      toast.error('Notification options could not be loaded. No confirmation was requested.');
      return false;
    }
  }, []);

  const send = async () => {
    if (!prompt || sendingRef.current || (!sms && !email)) return;
    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      await apiPost(operationsClient, prompt.endpoint, {
        channels: sms && email ? 'BOTH' : sms ? 'SMS' : 'EMAIL',
        ...(prompt.requestId ? { requestId: prompt.requestId } : {}),
      });
      toast.success('Confirmation request sent');
      sendingRef.current = false;
      close(true);
    } catch {
      setError('Confirmation could not be queued. Try again or choose Not now.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return {
    run,
    dialog: (
      <AlertDialog
        open={Boolean(prompt)}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {prompt?.resend ? 'Resend booking confirmation?' : 'Send booking confirmation?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {!prompt?.visit && 'Your job and visit are saved. '}Choose how to send the customer
              their scheduling confirmation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-3 rounded-lg border p-4">
              <Checkbox
                checked={sms}
                onCheckedChange={(value) => setSms(value === true)}
                disabled={!prompt?.phone || sending}
              />
              <span>
                <span className="block text-sm font-medium">Send SMS confirmation</span>
                <span className="text-sm text-muted-foreground">
                  {prompt?.phone ?? 'No available SMS number'}
                </span>
              </span>
            </label>
            <label className="flex items-center gap-3 rounded-lg border p-4">
              <Checkbox
                checked={email}
                onCheckedChange={(value) => setEmail(value === true)}
                disabled={!prompt?.email || sending}
              />
              <span>
                <span className="block text-sm font-medium">Send email confirmation</span>
                <span className="text-sm text-muted-foreground">
                  {prompt?.email ?? 'No available email address'}
                </span>
              </span>
            </label>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <Button type="button" variant="outline" disabled={sending} onClick={() => close()}>
              Not now
            </Button>
            <Button
              type="button"
              disabled={sending || (!sms && !email)}
              onClick={() => void send()}
            >
              {sending ? 'Queuing…' : sms && email ? 'Send both' : 'Send confirmation'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    ),
  };
}
